/* eslint-disable @stylistic/jsx-child-element-spacing */

'use client';

import { useScreenDetect } from '@sgcmpteltd/uikit-next';
import { Button, StarRating } from '@sgcmpteltd/uikit-react';
import {
  BUTTON_COLOR_RED,
  BUTTON_VARIANT_OUTLINED,
} from '@sgcmpteltd/uikit-react/components/button';
import { isRatingValue } from '@sgcmpteltd/uikit-react/components/star-rating';
import { convertToSnakeCase } from '@sgcmpteltd/uikit-react/utils/string';
import Image from 'next/image';
import { useRouter, useSearchParams } from 'next/navigation';
import { useContext, useState } from 'react';

import {
  TITLE_HEADING_H2,
  TITLE_HEADING_H3,
} from '@/components/section-container/base';
import {
  TITLE_HEADING_DIV,
} from '@/components/section-container/base/constants';
import { SectionContainer } from '@/components/section-container/page';
import { SortSelect } from '@/components/sort-select';
import { SITE_BASE_PATH } from '@/configs/site';
import { InfoContext } from '@/context/info';
import { UserContext } from '@/context/user';
import { joinSgcmMedia, joinSgcmMsSite } from '@/utils/url/join/public';

import { Alert } from '../../../_components/page-content/alert';
import {
  PAST_CAR_TERMINATED_PROMPT,
} from '../../../_components/page-content/alert/constants';
import {
  SECTION_TITLE_RECOMMENDED_FOR_YOU,
} from '../../../_components/page-content/constants';
import {
  EditorialReview,
} from '../../../_components/page-content/editorial-review';
import {
  RecommendedForYouForInfo,
} from '../../../_components/page-content/recommended-for-you-for-info';
import {
  SponsoredContent,
} from '../../../_components/page-content/sponsored-content';
import {
  getCompetitorPromotions,
} from '../../../_components/page-content/utils';
import {
  COMFORT_TEXT,
  EXTERIOR_TEXT,
  FEATURES_TEXT,
  HANDLING_TEXT,
  INTERIOR_TEXT,
  PERFORMANCE_TEXT,
  RELIABILITY_TEXT,
  VALUE_TEXT,
} from '../../../submit-review/_components/page-content/constants';
import { STAR_ICON_SIZE_IN_PIXEL } from '../../constants';
import { ConsumerReview } from '../consumer-review';
import { SortMenuItemsConfig } from './config';
import {
  NO_RESULTS_TEXT,
  NO_SEARCH_RESULTS,
  SUBMIT_REVIEW_BUTTON_TEXT,
} from './constants';
import styles from './styles.module.scss';
import type {
  Component,
  OrderBy,
  SortOrder,
  SortOrderValue,
  SortType,
} from './types';

const isBrowser = () => typeof window !== 'undefined';

export const PageContent: Component = ({
  editorialReviewList,
  consumerReviewList,
  modelId,
  slug,
  promotions,
}) => {
  const router = useRouter();
  const editorialReview = editorialReviewList[0];
  const { isIpadMini, isDesktop } = useScreenDetect();
  const isIpadMiniScreen = isIpadMini();
  const isDesktopScreen = isDesktop();
  const { carDetail } = useContext(InfoContext);
  const overallRate = carDetail?.review?.averageRating ?? 0;
  const userContextData = useContext(UserContext);
  const searchParams = useSearchParams();
  const [sorter, setSorter] = useState<SortOrder>(() => {
    const sortBy = searchParams.get('sort_by');
    const order = searchParams.get('order');
    return sortBy && order ? `${sortBy},${order}` as SortOrder : 'relevance,desc';
  });
  const rawUrl = isBrowser() ? window.location.href : '';
  const utf8Bytes = new TextEncoder().encode(rawUrl);
  const currentUrlInBase64 = Buffer.from(utf8Bytes).toString('base64');

  const formattedOverallRate = (
    rate?: number | null,
  ) => (rate ? rate.toFixed(1) : '0.0');
  const ratingValue = isRatingValue(overallRate) ? overallRate : null;

  const rateToPercent = (rate?: number): number => (rate
    ? rate * 20
    : 0);

  const getConsumerReviewOverallGrade = (points: number): string => {
    if (points >= 4.5) {
      return 'Excellent';
    }

    if ((points >= 4)) {
      return 'Good';
    }

    if ((points >= 3)) {
      return 'Average';
    }

    if ((points >= 2)) {
      return 'Fair';
    }

    return 'Poor';
  };

  const handleSubmitReviewButtonClick = () => {
    if (userContextData?.userId) {
      router.push(`${joinSgcmMsSite(SITE_BASE_PATH ?? '')}${`/info/${modelId}/${slug}/submit-review`}`);
    } else {
      window.open(
        joinSgcmMsSite(`/member/login?referrer=${currentUrlInBase64}`),
        '_self',
      );
    }
  };

  const handleSortChange = (sorterOrderValue: SortOrderValue) => {
    const searchData = sorterOrderValue.value === ''
      ? {
        query: {
          sortBy: undefined,
          order: undefined,
          page: undefined,
        },
      }
      : {
        query: {
          sortBy: sorterOrderValue.isChecked
            ? sorterOrderValue.value.split(',')[0] as SortType
            : undefined,
          order: sorterOrderValue.isChecked
            ? sorterOrderValue.value.split(',')[1] as OrderBy
            : undefined,
          page: undefined,
        },
      };

    const params = new URLSearchParams();

    const searchQueryEntries = Object.entries(searchData.query);

    for (const [key, value] of searchQueryEntries) {
      if (value !== undefined) {
        params.set(convertToSnakeCase(key), `${value}`);
      }
    }
    setSorter(sorterOrderValue.value);
    router.push(`/info/${carDetail.id}/${carDetail.slug}/reviews?${params.toString()}`);
  };

  const competitorPromotions = getCompetitorPromotions(
    promotions,
    carDetail.id,
  );

  const isPastCar = !(carDetail.additionalProperties.isAvailable
    || carDetail.additionalProperties.isUpcoming);

  return (
    (
      <main
        className={styles.containerPageRoot}>
        {
          isPastCar
          && (
            <SectionContainer
              title=""
              titleHeading={TITLE_HEADING_DIV}>
              <Alert
                date={carDetail.terminationDate}
                text={PAST_CAR_TERMINATED_PROMPT} />
            </SectionContainer>
          )
        }
        {
          editorialReview && (
            <SectionContainer
              className={styles.containerPageContent}
              title="Editorial Review"
              titleHeading={TITLE_HEADING_H2}>
              <EditorialReview
                make={carDetail.make}
                review={editorialReview} />
            </SectionContainer>
          )
        }
        {
          isDesktopScreen && competitorPromotions && (
            <SectionContainer
              title=""
              titleHeading={TITLE_HEADING_DIV}>
              <SponsoredContent
                modelId={modelId}
                promotions={promotions} />
            </SectionContainer>
          )
        }
        {
          consumerReviewList.length > 0 && (
            <>
              <SectionContainer
                className={styles.containerPageContent}
                title="Consumer Reviews"
                titleHeading={TITLE_HEADING_H3}>
                <div className={styles.containerConsumerOverallLayout}>
                  <div className={styles.containerOverallInfo}>
                    <div className={styles.containerOverallScoreFrame}>
                      <div className={styles.containerOverallScore}>
                        {formattedOverallRate(overallRate)}
                      </div>
                    </div>
                    <div className={styles.containerOverallRaing}>
                      <div className={styles.containerOverallRaingHead}>
                        <span className={styles.containerRatingCategory}>
                          {getConsumerReviewOverallGrade(overallRate)}
                        </span>
                        {' | '}
                        <span className={styles.userCount}>
                          {carDetail?.review?.count}
                          {' '}
                          Users
                          {isIpadMiniScreen && ' Reviews'}
                        </span>
                      </div>
                      <StarRating
                        iconSize={STAR_ICON_SIZE_IN_PIXEL}
                        value={ratingValue ?? 0} />
                    </div>
                    {
                      isIpadMiniScreen && (
                        <div className={styles.containerButton}>
                          <Button
                            className={styles.buttonSubmit}
                            color={BUTTON_COLOR_RED}
                            variant={BUTTON_VARIANT_OUTLINED}
                            onClick={() => {
                              handleSubmitReviewButtonClick();
                            }}>
                            <span className={
                              styles.submitText
                            }>
                              {SUBMIT_REVIEW_BUTTON_TEXT}
                            </span>
                          </Button>
                        </div>
                      )
                    }
                  </div>
                  <div className={styles.containerOverallEachRating}>
                    <div className={styles.containerBar}>
                      {
                        isIpadMiniScreen && (
                          <div className={styles.barWrapper}>
                            <div
                              className={styles.bar}
                              style={{
                                width: `${rateToPercent(
                                  carDetail?.review?.averageRatings?.comfort,
                                )}%`,
                              }} />
                          </div>
                        )
                      }
                      <div className={styles.containerScore}>
                        <span className={styles.score}>
                          {formattedOverallRate(
                            carDetail?.review?.averageRatings?.comfort,
                          )}
                        </span>
                        <span>
                          {COMFORT_TEXT}
                        </span>
                      </div>
                    </div>
                    <div className={styles.containerBar}>
                      {
                        isIpadMiniScreen && (
                          <div className={styles.barWrapper}>
                            <div
                              className={styles.bar}
                              style={{
                                width: `${rateToPercent(
                                  carDetail
                                    ?.review?.averageRatings?.reliability,
                                )}%`,
                              }} />
                          </div>
                        )
                      }
                      <div className={styles.containerScore}>
                        <span className={styles.score}>
                          {formattedOverallRate(
                            carDetail?.review?.averageRatings?.reliability,
                          )}
                        </span>
                        <span>
                          {RELIABILITY_TEXT}
                        </span>
                      </div>
                    </div>
                    <div className={styles.containerBar}>
                      {
                        isIpadMiniScreen && (
                          <div className={styles.barWrapper}>
                            <div
                              className={styles.bar}
                              style={{
                                width: `${rateToPercent(
                                  carDetail?.review?.averageRatings?.interior,
                                )}%`,
                              }} />
                          </div>
                        )
                      }
                      <div className={styles.containerScore}>
                        <span className={styles.score}>
                          {formattedOverallRate(
                            carDetail?.review?.averageRatings?.interior,
                          )}
                        </span>
                        <span>
                          {INTERIOR_TEXT}
                        </span>
                      </div>
                    </div>
                    <div className={styles.containerBar}>
                      {
                        isIpadMiniScreen && (
                          <div className={styles.barWrapper}>
                            <div
                              className={styles.bar}
                              style={{
                                width: `${rateToPercent(
                                  carDetail
                                    ?.review?.averageRatings?.valueForMoney,
                                )}%`,
                              }} />
                          </div>
                        )
                      }
                      <div className={styles.containerScore}>
                        <span className={styles.score}>
                          {formattedOverallRate(
                            carDetail?.review?.averageRatings?.valueForMoney,
                          )}
                        </span>
                        <span>
                          {
                            VALUE_TEXT
                          }
                        </span>
                      </div>
                    </div>
                    <div className={styles.containerBar}>
                      {
                        isIpadMiniScreen && (
                          <div className={styles.barWrapper}>
                            <div
                              className={styles.bar}
                              style={{
                                width: `${rateToPercent(
                                  carDetail?.review?.averageRatings?.handling,
                                )}%`,
                              }} />
                          </div>
                        )
                      }
                      <div className={styles.containerScore}>
                        <span className={styles.score}>
                          {formattedOverallRate(
                            carDetail?.review?.averageRatings?.handling,
                          )}
                        </span>
                        <span>
                          {HANDLING_TEXT}
                        </span>
                      </div>
                    </div>
                    <div className={styles.containerBar}>
                      {
                        isIpadMiniScreen && (
                          <div className={styles.barWrapper}>
                            <div
                              className={styles.bar}
                              style={{
                                width: `${rateToPercent(
                                  carDetail?.review?.averageRatings?.features,
                                )}%`,
                              }} />
                          </div>
                        )
                      }
                      <div className={styles.containerScore}>
                        <span className={styles.score}>
                          {formattedOverallRate(
                            carDetail?.review?.averageRatings?.features,
                          )}
                        </span>
                        <span>
                          {FEATURES_TEXT}
                        </span>
                      </div>
                    </div>
                    <div className={styles.containerBar}>
                      {
                        isIpadMiniScreen && (
                          <div className={styles.barWrapper}>
                            <div
                              className={styles.bar}
                              style={{
                                width: `${rateToPercent(
                                  carDetail
                                    ?.review?.averageRatings?.performance,
                                )}%`,
                              }} />
                          </div>
                        )
                      }
                      <div className={styles.containerScore}>
                        <span className={styles.score}>
                          {formattedOverallRate(
                            carDetail?.review?.averageRatings?.performance,
                          )}
                        </span>
                        <span>
                          {PERFORMANCE_TEXT}
                        </span>
                      </div>
                    </div>
                    <div className={styles.containerBar}>
                      {
                        isIpadMiniScreen && (
                          <div className={styles.barWrapper}>
                            <div
                              className={styles.bar}
                              style={{
                                width: `${rateToPercent(
                                  carDetail?.review?.averageRatings?.exterior,
                                )}%`,
                              }} />
                          </div>
                        )
                      }
                      <div className={styles.containerScore}>
                        <span className={styles.score}>
                          {formattedOverallRate(
                            carDetail?.review?.averageRatings?.exterior,
                          )}
                        </span>
                        <span>
                          {EXTERIOR_TEXT}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className={styles.containerButton}>
                    {!isIpadMiniScreen && (
                      <Button
                        className={styles.buttonSubmit}
                        color={BUTTON_COLOR_RED}
                        variant={BUTTON_VARIANT_OUTLINED}
                        onClick={() => {
                          handleSubmitReviewButtonClick();
                        }}>
                        <span className={
                          styles.submitText
                        }>
                          {SUBMIT_REVIEW_BUTTON_TEXT}
                        </span>
                      </Button>
                    )}
                  </div>
                </div>
              </SectionContainer>
              <SectionContainer
                className={styles.containerSort}
                title=""
                titleHeading={TITLE_HEADING_DIV}>
                <SortSelect
                  desktopSortMenuItemsConfig={SortMenuItemsConfig}
                  mobileSortMenuItemsConfig={SortMenuItemsConfig}
                  sorter={sorter}
                  onSortChange={handleSortChange} />
              </SectionContainer>
              <SectionContainer
                className={styles.containerConsumerReviews}
                title=""
                titleHeading={TITLE_HEADING_DIV}>
                {
                  consumerReviewList.map(consumerReview => (
                    <ConsumerReview
                      key={consumerReview.id}
                      consumerReview={consumerReview} />
                  ))
                }
              </SectionContainer>
            </>
          )
        }
        {
          consumerReviewList.length === 0 && (
            <SectionContainer
              className={styles.containerNoSearchResult}
              title="Consumer Reviews"
              titleHeading={TITLE_HEADING_H3}>
              <Image
                alt="Mangifier with a question mark inside over an empty folder"
                height={144}
                src={`${joinSgcmMedia('/public/new-cars/no-search-results.svg')}`}
                width={164} />
              <div className={styles.noResultsFoundText}>
                {NO_SEARCH_RESULTS}
              </div>
              <div className={styles.noReviewsText}>
                {NO_RESULTS_TEXT}
              </div>
              <Button
                className={styles.buttonSubmit}
                color={BUTTON_COLOR_RED}
                variant={BUTTON_VARIANT_OUTLINED}
                onClick={() => {
                  handleSubmitReviewButtonClick();
                }}>
                <span className={
                  styles.submitText
                }>
                  {SUBMIT_REVIEW_BUTTON_TEXT}
                </span>
              </Button>
            </SectionContainer>
          )
        }
        <SectionContainer
          className={styles.containerPageContent}
          title={SECTION_TITLE_RECOMMENDED_FOR_YOU}
          titleHeading={TITLE_HEADING_H2}>
          <RecommendedForYouForInfo />
        </SectionContainer>
      </main>
    )
  );
};
